import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';

import { updateUserPreferences } from 'api/Preferences';

import { iaRa } from 'lib/helpers/utility';
import { useUserPreferences } from 'lib/hooks/queries/user/useUserPreferences';

import { ONBOARDING_TOUR_COMPLETED_KEY } from '../lib/constants';

export default function useStepperTour({ isAuthenticated, wizardInstanceId }) {
  const navigate = useNavigate();
  const [showTour, setShowTour] = useState(false);

  const { data: preferencesData, refetch: refetchPreferences } = useUserPreferences({
    params: { name: ONBOARDING_TOUR_COMPLETED_KEY },
    dependencies: [ONBOARDING_TOUR_COMPLETED_KEY],
    options: { enabled: isAuthenticated }
  });

  const seenInstanceIds = useMemo(
    () => iaRa(preferencesData?.preferences?.value?.seenInstances),
    [preferencesData]
  );

  const hasSeenTour = useMemo(
    () => !!wizardInstanceId && seenInstanceIds.includes(wizardInstanceId),
    [wizardInstanceId, seenInstanceIds]
  );

  const startTourIfUnseen = useCallback(() => {
    if (wizardInstanceId && !hasSeenTour) {
      setShowTour(true);
    }
  }, [wizardInstanceId, hasSeenTour]);

  const handleTourClose = useCallback(async () => {
    setShowTour(false);
    if (isAuthenticated && wizardInstanceId && !hasSeenTour) {
      const updatedValue = { seenInstances: [...seenInstanceIds, wizardInstanceId] };
      try {
        await updateUserPreferences(navigate, {
          name: ONBOARDING_TOUR_COMPLETED_KEY,
          value: updatedValue
        });
        refetchPreferences();
      } catch (error) {
        Honeybadger.notify(error);
      }
    }
  }, [
    isAuthenticated,
    wizardInstanceId,
    hasSeenTour,
    seenInstanceIds,
    navigate,
    refetchPreferences
  ]);

  return { showTour, startTourIfUnseen, handleTourClose };
}
